/**
 * Simulation events — the one-way channel from rules to presentation.
 *
 * The sim never calls into the renderer, the UI or the audio graph. It appends typed
 * events to a per-tick queue; the presentation layer drains that queue and turns each
 * entry into particles, sound and camera shake. This is what lets the whole game run
 * headless in tests: the event stream *is* the observable behaviour.
 *
 * Every event carries a world position where one makes sense, because almost every
 * consumer needs it — distance attenuation, delayed audio, splash placement, shake.
 */

import type { UpgradeId } from '../content/economy';
import type { Vec3 } from './math';
import type { EntityId, ProjectileKind, VesselKind, WeaponId } from './types';

export type SimEvent =
  /** A player weapon discharged. */
  | { type: 'WeaponFired'; weapon: WeaponId; pos: Vec3; dir: Vec3; hostile: false }
  /**
   * A hostile weapon discharged. The hull kind lets presentation put the flash at the
   * visible gun or launcher without teaching the deterministic simulation about meshes.
   */
  | {
      type: 'WeaponFired';
      weapon: 'hostile';
      pos: Vec3;
      dir: Vec3;
      hostile: true;
      sourceKind: VesselKind;
      /** Hull bearing, radians, so presentation can transform a local weapon hardpoint. */
      sourceHeading: number;
    }
  /** A projectile struck water. `speed` scales the splash column. */
  | { type: 'ImpactWater'; pos: Vec3; kind: ProjectileKind; speed: number; hostile: boolean;
      /** True when the round skipped rather than sank — a smaller, flatter splash. */
      ricochet: boolean }
  /** A projectile struck a hull. */
  | { type: 'ImpactHull'; pos: Vec3; kind: ProjectileKind; targetId: EntityId; damage: number; ricochet: boolean }
  /** A large detonation (torpedo, depth charge, magazine). */
  | {
      type: 'Detonation';
      pos: Vec3;
      radius: number;
      underwater: boolean;
      /** Metres below the surface, so the FX can scale the eight-beat sequence by depth. */
      depth: number;
    }
  /** A friendly vessel crossed the safety ring. */
  | { type: 'ShipSafe'; id: EntityId; kind: VesselKind; pos: Vec3 }
  /** Cargo finished transferring. */
  | { type: 'CargoDelivered'; id: EntityId; summary: string }
  /**
   * Equipment came off a ship and is now fitted. The sim applies it to the running watch
   * immediately; the app is what makes it outlast the shift, by writing it to the save.
   */
  | { type: 'UpgradeFitted'; upgrade: UpgradeId }
  /** A vessel was destroyed. */
  | { type: 'ShipLost'; id: EntityId; kind: VesselKind; faction: 'friendly' | 'hostile'; pos: Vec3 }
  /** The rig took damage. */
  /**
   * A Kraken phase turned, or it attacked. Presentation-only: the phase machine is the
   * authority, this just lets the renderer and the mix react to it.
   */
  | { type: 'KrakenPhase'; phase: string; pos: Vec3; slam: boolean }
  /** `cause` picks the voice: incoming rounds ricochet, a creature on the legs clangs. */
  | { type: 'RigHit'; pos: Vec3; damage: number; casualties: number; cause: 'gunfire' | 'creature' }
  /** A new contact entered detection range. */
  | { type: 'ContactNew'; id: EntityId; kind: VesselKind; hostile: boolean }
  /** A contact was identified (silhouette → identified). */
  | { type: 'ContactIdentified'; id: EntityId; kind: VesselKind }
  /** The shift's objective text changed — the mission banner listens for this. */
  | { type: 'Objective'; text: string }
  /** A radio/dialogue line. */
  /**
   * A radio/dialogue line. `read` is the baked performance when the two differ — either
   * because the line carries direction, or because `text` is assembled at runtime and
   * therefore cannot have been recorded. See `content/radio.ts`.
   */
  /**
   * `final` marks the shift's sign-off: the last thing the channel says. The mix uses it
   * to drop whatever chatter is still queued behind it and to refuse anything after, so
   * the voice cannot run on over the results screen — see `AudioEngine.radioFinal`.
   */
  | { type: 'Radio'; text: string; speaker: string; read?: string; final?: boolean }
  /** The shift ended. */
  | { type: 'ShiftEnd'; outcome: 'complete' | 'defeat' }
  /** Reload started or finished. */
  | {
      type: 'WeaponState';
      weapon: WeaponId;
      /**
       * `outOfEnvelope` is a refusal, not a failure: the depth-charge projector will not
       * throw a charge short of 150 m or past its rail limit. Plan D3 is explicit that
       * the player is told, never given a silent dud shot.
       */
      state: 'reloading' | 'ready' | 'empty' | 'outOfEnvelope' | 'noLock';
    };

export type SimEventType = SimEvent['type'];

/**
 * A simple append-and-drain queue. The sim appends during a tick; the presentation layer
 * drains once per frame. Draining transfers ownership, so events are never processed
 * twice and the queue cannot grow unbounded if a frame is slow.
 */
export class EventQueue {
  private events: SimEvent[] = [];

  emit(e: SimEvent): void {
    this.events.push(e);
  }

  /** Remove and return everything queued. */
  drain(): SimEvent[] {
    if (this.events.length === 0) return [];
    const out = this.events;
    this.events = [];
    return out;
  }

  /** Read without removing — for tests and assertions. */
  peek(): readonly SimEvent[] {
    return this.events;
  }

  clear(): void {
    this.events.length = 0;
  }

  get length(): number {
    return this.events.length;
  }
}
